import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import SelectDropDown from "@/components/Common/SelectDropDown";
import { PICKER_STYLES } from "@/helpers/pickerStyles";
import "./BrokerageAgentOrder.scss";

export const ORDER_OPTIONS = [
  { value: "activity", label: "Most active buyers first" },
  { value: "custom", label: "Custom order, drag to arrange" },
];

const PAGE_SIZE = 25;

export const customOrderOf = (agents, savedIds) => {
  const byId = new Map(agents.map((agent) => [agent.userId, agent]));
  const saved = savedIds.filter((userId) => byId.has(userId));
  const rest = agents.map((agent) => agent.userId).filter((userId) => !saved.includes(userId));
  return [...saved, ...rest];
};

export const moveId = (ids, userId, targetIndex) => {
  const from = ids.indexOf(userId);
  if (from < 0 || targetIndex < 0 || targetIndex >= ids.length || from === targetIndex) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(targetIndex, 0, userId);
  return next;
};

const Switch = ({ label, checked, disabled, onChange }) => (
  <label className={`agent-order__switch${disabled ? " is-disabled" : ""}`}>
    <input
      type="checkbox"
      role="switch"
      className="agent-order__switch-input"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="agent-order__switch-track" aria-hidden="true" />
    <span>{label}</span>
  </label>
);

const GripIcon = () => (
  <svg viewBox="0 0 10 16" width="10" height="16" aria-hidden="true">
    {[3, 8, 13].map((y) => (
      <React.Fragment key={y}>
        <circle cx="2.5" cy={y} r="1.4" />
        <circle cx="7.5" cy={y} r="1.4" />
      </React.Fragment>
    ))}
  </svg>
);

const BrokerageAgentOrder = ({ data, saveOrder, saveHidden }) => {
  const agents = data.agents || [];
  const known = new Set(agents.map((agent) => agent.userId));
  const hidden = (data.hidden || []).filter((userId) => known.has(userId));
  const mode = data.agentOrder?.mode === "custom" ? "custom" : "activity";
  const savedIds = data.agentOrder?.userIds || [];
  const [order, setOrder] = useState(() => customOrderOf(agents, savedIds));
  const [find, setFind] = useState("");
  const [shown, setShown] = useState(PAGE_SIZE);
  const [dragId, setDragId] = useState(null);
  const [overId, setOverId] = useState(null);
  const [announce, setAnnounce] = useState("");
  const listRef = useRef(null);
  const savedKey = `${agents.map((agent) => agent.userId).join(",")}|${savedIds.join(",")}`;

  useEffect(() => {
    setOrder(customOrderOf(agents, savedIds));
  }, [savedKey]);

  const fail = (error) => toast.error(error.message);
  const byId = new Map(agents.map((agent) => [agent.userId, agent]));
  const nameOf = (userId) => byId.get(userId)?.name || "";
  const term = find.trim().toLowerCase();
  const custom = mode === "custom";
  const busy = saveOrder.isPending || saveHidden.isPending;

  const commit = (next) => {
    const previous = order;
    setOrder(next);
    saveOrder.mutate(
      { mode: "custom", userIds: next.filter((userId) => !hidden.includes(userId)) },
      {
        onError: (error) => {
          setOrder(previous);
          fail(error);
        },
      },
    );
  };

  const setMode = (value) => {
    if (value === mode) return;
    saveOrder.mutate(
      value === "custom" ? { mode: value, userIds: order.filter((userId) => !hidden.includes(userId)) } : { mode: value },
      { onError: fail },
    );
  };

  const setShownFor = (userId, on) =>
    saveHidden.mutate(on ? hidden.filter((id) => id !== userId) : [...hidden, userId], { onError: fail });

  const visibleIds = custom ? order.filter((userId) => !hidden.includes(userId)) : agents.map((agent) => agent.userId).filter((userId) => !hidden.includes(userId));
  const ordered = [...visibleIds, ...hidden].filter((userId) => !term || nameOf(userId).toLowerCase().includes(term));
  const listed = custom && !term ? ordered : ordered.slice(0, shown);
  const canDrag = custom && !term;

  const moveBy = (userId, step) => {
    const index = visibleIds.indexOf(userId);
    const next = moveId(order.filter((id) => !hidden.includes(id)), userId, index + step);
    if (next.indexOf(userId) === index) return;
    commit([...next, ...hidden]);
    setAnnounce(`${nameOf(userId)} is now number ${index + step + 1} of ${visibleIds.length}.`);
    requestAnimationFrame(() => listRef.current?.querySelector(`[data-grip="${userId}"]`)?.focus());
  };

  const targetAt = (x, y) => {
    const row = document.elementFromPoint(x, y)?.closest?.("[data-agent-id]");
    return row ? Number(row.getAttribute("data-agent-id")) : null;
  };

  const finishDrag = (targetId) => {
    const dragged = dragId;
    setDragId(null);
    setOverId(null);
    if (dragged === null || targetId === null || targetId === dragged || hidden.includes(targetId)) return;
    const next = moveId(visibleIds, dragged, visibleIds.indexOf(targetId));
    commit([...next, ...hidden]);
    setAnnounce(`${nameOf(dragged)} is now number ${next.indexOf(dragged) + 1} of ${next.length}.`);
  };

  const gripProps = (userId, index) => ({
    type: "button",
    className: "agent-order__grip",
    "data-grip": userId,
    "aria-label": `Reorder ${nameOf(userId)}, number ${index + 1} of ${visibleIds.length}. Use the up and down arrow keys.`,
    "aria-disabled": busy && dragId === null ? "true" : undefined,
    onKeyDown: (event) => {
      if (busy) return;
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        moveBy(userId, event.key === "ArrowUp" ? -1 : 1);
      }
    },
    onPointerDown: (event) => {
      if (busy || (event.button !== undefined && event.button !== 0)) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      setDragId(userId);
    },
    onPointerMove: (event) => {
      if (dragId === null) return;
      const target = targetAt(event.clientX, event.clientY);
      if (target !== overId) setOverId(target);
    },
    onPointerUp: (event) => {
      if (dragId === null) return;
      finishDrag(targetAt(event.clientX, event.clientY));
    },
    onPointerCancel: () => {
      setDragId(null);
      setOverId(null);
    },
  });

  return (
    <section className="content-block agent-order">
      <div className="title-sec">
        <h4>Agents on your page</h4>
      </div>
      <div className="agent-order__mode">
        <span className="agent-order__label">Agent order</span>
        <SelectDropDown
          id="agent-order-mode"
          className="agent-order__select"
          classNamePrefix="select"
          options={ORDER_OPTIONS}
          value={ORDER_OPTIONS.find((option) => option.value === mode)}
          disabled={busy}
          styles={PICKER_STYLES}
          errorClass="d-none"
          onChange={(option) => option && setMode(option.value)}
        />
      </div>
      {agents.length > 8 ? (
        <input
          className="agent-order__find"
          type="search"
          value={find}
          onChange={(event) => {
            setFind(event.target.value);
            setShown(PAGE_SIZE);
          }}
          placeholder="Find an agent"
          aria-label="Find an agent"
        />
      ) : null}
      <ol ref={listRef} className={`agent-order__list${canDrag ? " can-drag" : ""}${dragId !== null ? " is-dragging" : ""}`}>
        {listed.map((userId) => {
          const isHidden = hidden.includes(userId);
          const index = visibleIds.indexOf(userId);
          return (
            <li
              key={userId}
              data-agent-id={userId}
              className={`agent-order__row${isHidden ? " is-hidden" : ""}${dragId === userId ? " is-lifted" : ""}${
                overId === userId && dragId !== userId ? " is-over" : ""
              }`}
            >
              {canDrag && !isHidden ? (
                <button {...gripProps(userId, index)}>
                  <GripIcon />
                </button>
              ) : null}
              <span className="agent-order__name">{nameOf(userId)}</span>
              <Switch label={isHidden ? "Not on your page" : "On your page"} checked={!isHidden} disabled={busy} onChange={(on) => setShownFor(userId, on)} />
            </li>
          );
        })}
      </ol>
      <p className="visually-hidden" aria-live="polite">
        {announce}
      </p>
      {!ordered.length && term ? (
        <p className="agent-order__hint">No agent named &ldquo;{find.trim()}&rdquo; on your&nbsp;roster.</p>
      ) : null}
      {listed.length < ordered.length ? (
        <button type="button" className="agent-order__more" onClick={() => setShown(shown + PAGE_SIZE)}>
          Show more agents
        </button>
      ) : null}
    </section>
  );
};

export default BrokerageAgentOrder;
